import { computed, unref } from 'vue';

export default function useLiferayLocale(fallback = 'en-US') {
    /**
     * Locale including region, separated with underscore ("_").
     *
     * @type {ComputedRef<unknown>}
     */
    const cldrLocale = computed(() => {
        if (window.Liferay && window.Liferay.ThemeDisplay && window.Liferay.ThemeDisplay) {
            return window.Liferay.ThemeDisplay.getLanguageId();
        }

        return fallback.replace('-', '_');
    });

    /**
     * Locale including region, separated with dash.
     *
     * @type {ComputedRef<unknown>}
     */
    const locale = computed(() => {
        if (window.Liferay && window.Liferay.ThemeDisplay && window.Liferay.ThemeDisplay) {
            return window.Liferay.ThemeDisplay.getBCP47LanguageId();
        }

        return fallback.replace('_', '-');
    });

    /**
     * Language key of the current locale.
     *
     * @type {ComputedRef<unknown>}
     */
    const languageKey = computed(() => {
        if (locale.value !== null) {
            return locale.value.split('-')[0];
        }

        return null;
    });

    /**
     * Region of the current locale.
     *
     * @type {ComputedRef<unknown>}
     */
    const region = computed(() => {
        if (locale.value !== null) {
            return locale.value.split('-')[1];
        }

        return null;
    });

    /** @type {ComputedRef<string | string>} */
    const urlLocaleParameter = computed(() => {
        return languageKey.value === 'en' || languageKey.value === 'zh' ? '' : `/${languageKey.value}`;
    });

    const flattenLocalized = object =>
        object.map(o => {
            if (Object.hasOwn(o, 'titles')) {
                if (Object.hasOwn(o.titles, unref(cldrLocale))) {
                    return o.titles[unref(cldrLocale)];
                }

                if (Object.hasOwn(o.titles, 'en_US')) {
                    return o.titles['en_US'];
                }
            }

            return o.name;
        });

    const availableLanguages = computed(() => {
        return window.Liferay?.Language?.available  || {};
    });

    const availableLocales = computed(() => {
        return Object.keys(unref(availableLanguages)).map(key => key.split('_')[0].toUpperCase());
    });

    return {
        locale,
        cldrLocale,
        languageKey,
        region,
        flattenLocalized,
        urlLocaleParameter,
        availableLanguages,
        availableLocales,
    };
}
